/**
 * InlinePriceOverride
 *
 * A compact lock-button + inline PIN popover that appears next to a
 * non-editable price field in /sales-new (Quick and Detailed modes).
 *
 * When the cashier has no `sales.edit_price` permission, the price field
 * is locked. Clicking this button opens a small popover where a manager
 * or admin enters their PIN to authorise a one-time price edit on that
 * specific line item.
 *
 * Props:
 *   branchId  – current branch (sent to verify endpoint for branch-specific PINs)
 *   onUnlock  – (verifierName: string, reason: string) => void
 *               called after successful PIN verification
 */
import { useState, useRef, useEffect } from 'react';
import { api } from '../contexts/AuthContext';
import { Lock, Unlock, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { pinFieldProps } from '../lib/pinField';

const REASONS = [
  { key: 'discount',   label: 'Discount' },
  { key: 'correction', label: 'Price fix' },
  { key: 'promo',      label: 'Promo' },
  { key: 'wholesale',  label: 'Wholesale' },
  { key: 'other',      label: 'Other' },
];

export default function InlinePriceOverride({ branchId, onUnlock }) {
  const [open, setOpen]       = useState(false);
  const [pin, setPin]         = useState('');
  const [reason, setReason]   = useState('discount');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const containerRef          = useRef(null);
  const pinInputRef           = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setPin('');
        setError('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Auto-focus PIN field when popover opens
  useEffect(() => {
    if (open) setTimeout(() => pinInputRef.current?.focus(), 60);
  }, [open]);

  const handleOpen = (e) => {
    e.stopPropagation();
    setOpen(v => !v);
    setError('');
    setPin('');
  };

  const handleAuthorize = async () => {
    const trimmed = pin.trim();
    if (!trimmed) { setError('Enter manager or admin PIN'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/verify/verify-pin-action', {
        pin: trimmed,
        action: 'pos_price_override',
        branch_id: branchId || '',
      });
      const verifierName = res.data.verified_by || 'Manager';
      setOpen(false);
      setPin('');
      toast.success(`Price override authorised by ${verifierName}`);
      onUnlock(verifierName, reason);
    } catch (e) {
      setError(e.response?.data?.detail || 'Invalid PIN — not authorised');
    }
    setLoading(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Lock trigger button */}
      <button
        type="button"
        onMouseDown={e => e.stopPropagation()}
        onClick={handleOpen}
        className="flex items-center gap-0.5 h-7 px-1.5 text-slate-400 hover:text-[#1A4D2E] hover:bg-emerald-50 rounded border border-slate-200 hover:border-emerald-300 transition-colors"
        title="Override price — manager / admin PIN required"
        data-testid="price-override-btn"
      >
        <Lock size={11} />
        <span className="text-[10px] font-medium leading-none">Override</span>
      </button>

      {/* Inline PIN popover */}
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-[300] bg-white border border-slate-200 rounded-xl shadow-2xl w-64 p-3 space-y-2.5"
          onMouseDown={e => e.stopPropagation()}
          data-testid="price-override-popover"
        >
          <div className="flex items-center gap-1.5">
            <Unlock size={12} className="text-[#1A4D2E]" />
            <p className="text-xs font-semibold text-slate-800">Override Price</p>
          </div>

          {/* Reason chips */}
          <div className="flex flex-wrap gap-1">
            {REASONS.map(r => (
              <button
                key={r.key}
                type="button"
                onClick={() => setReason(r.key)}
                className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                  reason === r.key
                    ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          {/* PIN field */}
          <input
            ref={pinInputRef}
            {...pinFieldProps()}
            value={pin}
            onChange={e => { setPin(e.target.value); setError(''); }}
            onKeyDown={e => {
              if (e.key === 'Enter') handleAuthorize();
              if (e.key === 'Escape') { setOpen(false); setPin(''); setError(''); }
            }}
            placeholder="Manager / Admin PIN"
            className="w-full h-8 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30 focus:border-[#1A4D2E] tracking-widest"
            data-testid="price-override-pin-input"
          />

          {error && (
            <p className="text-[10px] text-red-600 flex items-center gap-1" data-testid="price-override-error">
              <AlertTriangle size={10} className="shrink-0" /> {error}
            </p>
          )}

          <div className="flex gap-2 pt-0.5">
            <button
              type="button"
              onClick={() => { setOpen(false); setPin(''); setError(''); }}
              className="flex-1 h-7 text-xs text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAuthorize}
              disabled={loading || !pin.trim()}
              className="flex-1 h-7 text-xs font-semibold bg-[#1A4D2E] text-white rounded-lg hover:bg-[#14532d] disabled:opacity-50 flex items-center justify-center gap-1 transition-colors"
              data-testid="price-override-authorize-btn"
            >
              {loading && <RefreshCw size={10} className="animate-spin" />}
              Authorize
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
